import { useEffect, useState } from "react";
import { AppShell } from "@/components/Nav";
import { PlusGate } from "@/components/PlusGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, Sparkles, Copy, Download } from "lucide-react";

const SECTIONS = [
  { key: "ftc_affidavit", label: "FTC Identity Theft Affidavit", filename: "ftc-affidavit.txt" },
  { key: "fcra_605b_block_letter", label: "FCRA §605B block letter", filename: "fcra-605b-block.txt" },
  { key: "police_report_template", label: "Police report narrative", filename: "police-report.txt" },
];

export default function IdTheftKit() {
  const { user } = useAuth();
  const isPlus = user?.plan === "plus";

  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState("");
  const [incident, setIncident] = useState("");
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isPlus) return;
    (async () => {
      try {
        const { data } = await api.get("/negative-items");
        setItems(data);
        if (data.length && !selectedItem) setSelectedItem(data[0].id);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, [isPlus]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedItem) {
      toast.error("Pick the fraudulent item first");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/plus/idtheft-kit", {
        negative_item_id: selectedItem,
        incident_summary: incident || null,
        approximate_date: date || null,
      });
      setResult(data);
      toast.success("Kit generated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const copySection = (key) => {
    navigator.clipboard.writeText(result.kit[key]);
    toast.success("Copied");
  };

  const downloadSection = (key, filename) => {
    const blob = new Blob([result.kit[key]], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppShell>
      <div className="mb-10">
        <div className="overline mb-3 text-[#D5FF3F]">Plus tool</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">Identity-theft kit.</h1>
        <p className="text-[#A8ADA5] mt-3 max-w-2xl">
          One click generates the FTC affidavit, the FCRA §605B block-letter, a police-report template, and your
          next-steps checklist for a fraudulent account.
        </p>
      </div>

      {!isPlus ? (
        <PlusGate
          toolName="The identity-theft kit"
          tagline="Everything you need to nuke a fraudulent account off your credit — FTC affidavit + §605B block letter + police report + step-by-step. Plus subscribers only."
        />
      ) : (
        <>
          <form onSubmit={submit} className="card-surface p-8 space-y-5 mb-6" data-testid="idtheft-form">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Fraudulent item</Label>
              <Select value={selectedItem} onValueChange={setSelectedItem}>
                <SelectTrigger data-testid="idtheft-item-select" className="mt-1 rounded-sm bg-[#0B0C0E] border-[#26282C]">
                  <SelectValue placeholder="Choose an item" />
                </SelectTrigger>
                <SelectContent className="bg-[#131518] border-[#26282C] text-[#F5F3EE]">
                  {items.length === 0 ? (
                    <SelectItem value="none" disabled>No items yet — add one first</SelectItem>
                  ) : items.map((it) => (
                    <SelectItem key={it.id} value={it.id}>
                      {it.creditor} · {(it.item_type || "").replace("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Approximate date discovered</Label>
              <Input
                placeholder="e.g. Jan 2026"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="idtheft-date"
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">What happened? (optional)</Label>
              <Textarea
                rows={4}
                placeholder="e.g. I received a collections notice for an account I never opened. My wallet was stolen in December."
                value={incident}
                onChange={(e) => setIncident(e.target.value)}
                data-testid="idtheft-incident"
                className="mt-1 rounded-sm"
              />
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={loading || items.length === 0}
                data-testid="idtheft-submit-btn"
                className="rounded-full btn-lime h-11 px-6"
              >
                {loading ? (
                  <><Sparkles size={15} className="mr-2 animate-pulse" /> Generating…</>
                ) : (
                  <><ShieldAlert size={15} className="mr-2" /> Generate kit</>
                )}
              </Button>
            </div>
          </form>

          {result && (
            <div className="fade-in space-y-6" data-testid="idtheft-result">
              {SECTIONS.map((s) => (
                result.kit?.[s.key] ? (
                  <div key={s.key} className="card-surface p-8">
                    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                      <div>
                        <div className="overline mb-1">Document</div>
                        <div className="font-display text-2xl">{s.label}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => copySection(s.key)} className="rounded-sm btn-outline">
                          <Copy size={13} className="mr-2" /> Copy
                        </Button>
                        <Button onClick={() => downloadSection(s.key, s.filename)} className="rounded-sm btn-outline">
                          <Download size={13} className="mr-2" /> Download
                        </Button>
                      </div>
                    </div>
                    <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed text-[#F5F3EE] max-h-96 overflow-y-auto">
{result.kit[s.key]}
                    </pre>
                  </div>
                ) : null
              ))}

              {result.kit?.next_steps?.length > 0 && (
                <div className="card-surface p-8">
                  <div className="overline mb-6">Next steps · in order</div>
                  <ol className="space-y-4">
                    {result.kit.next_steps.map((step, i) => (
                      <li key={`${i}-${(step || "step").slice(0, 40)}`} className="flex items-start gap-4">
                        <span className="ticker text-2xl text-[#D5FF3F] leading-none w-8 flex-shrink-0">
                          {(i + 1).toString().padStart(2, "0")}
                        </span>
                        <span className="text-[#F5F3EE] leading-relaxed">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </AppShell>
  );
}
